/**
 * Call shapes the Geotab API silently ignores or rejects, checked before a call is sent.
 *
 * Sources:
 * - GetFeed types and which search each feed applies:
 *   https://developers.geotab.com/myGeotab/apiReference/methods/GetFeed
 * - Search object properties: https://developers.geotab.com/myGeotab/apiReference/objects/RuleSearch,
 *   .../objects/GroupSearch, .../objects/UserSearch, .../objects/TextMessageSearch
 * - propertySelector is a top-level Get/GetFeed parameter, not a search property:
 *   https://developers.geotab.com/myGeotab/guides/concepts (Property Selector)
 */

/** Every typeName GetFeed supports (M GetFeed, "Supported entity types"). */
const GET_FEED_TYPES: ReadonlySet<string> = new Set([
  "AnnotationLog",
  "Audit",
  "ChargeEvent",
  "CustomData",
  "DebugData",
  "Device",
  "DeviceShare",
  "DeviceStatusInfo",
  "Diagnostic",
  "Driver",
  "DriverChange",
  "DutyStatusLog",
  "DVIRLog",
  "EmissionComplianceEvent",
  "EmissionVehicleEnrollment",
  "ExceptionEvent",
  "FailureMode",
  "FaultData",
  "FillUp",
  "FuelAndEnergyUsed",
  "FuelTaxDetail",
  "FuelUsed",
  "IoxAddOn",
  "LogRecord",
  "MediaFile",
  "Route",
  "Rule",
  "ShipmentLog",
  "StatusData",
  "TachographDataFile",
  "TextMessage",
  "Trailer",
  "TrailerAttachment",
  "Trip",
  "User",
  "Zone",
]);

/**
 * Feeds that ignore their search, or apply only the listed properties of it (M GetFeed). A feed not
 * listed here applies its whole search object.
 */
const GET_FEED_APPLIED_SEARCH: Readonly<Record<string, readonly string[]>> = {
  DebugData: ["fromDate"],
  Device: [],
  DeviceShare: [],
  DeviceStatusInfo: ["diagnostics"],
  Diagnostic: [],
  Driver: [],
  FailureMode: [],
  LogRecord: [],
  MediaFile: [],
  Rule: [],
  StatusData: [],
  Trailer: [],
  Trip: ["fromDate"],
  User: [],
  Zone: [],
};

/** Search properties a type's search object does not have (O <Type>Search). */
const UNKNOWN_SEARCH_PROPERTIES: Readonly<Record<string, readonly string[]>> = {
  Driver: ["groupFilterCondition"],
  Group: ["ids"],
  Rule: ["ids"],
  TextMessage: ["groupFilterCondition"],
  User: ["groupFilterCondition"],
};

/**
 * One problem with a call. `rejectedByServer`: the server fails the call (and with it the whole
 * `ExecuteMultiCall` it is batched into); otherwise the server silently ignores the offending part.
 * Live-verified 2026-10-14 (#101): GetFeed User with a search, GetFeed LogRecord with deviceSearch,
 * Get Group with an `id` array. GetFeed on an unsupported type follows the GetFeed docs.
 */
type Violation = { message: string; rejectedByServer: boolean };

const rejected = (message: string): Violation => ({ message, rejectedByServer: true });
const ignored = (message: string): Violation => ({ message, rejectedByServer: false });

/** Thrown by {@link assertGeotabCallShape}; lists every problem found in one call. */
export class GeotabCallShapeError extends Error {
  readonly violations: readonly string[];
  /** True when the server would fail the call, so it must never be sent. */
  readonly rejectedByServer: boolean;

  constructor(
    readonly method: string,
    violations: readonly Violation[],
  ) {
    const messages = violations.map(violation => violation.message);
    super(`Invalid Geotab ${method} call: ${messages.join("; ")}`);
    this.name = "GeotabCallShapeError";
    this.violations = messages;
    this.rejectedByServer = violations.some(violation => violation.rejectedByServer);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Driver is a User: Get/GetFeed Driver take a UserSearch. */
function searchName(typeName: string): string {
  return `${typeName === "Driver" ? "User" : typeName}Search`;
}

/** Keys JSON serialisation keeps: a property set to `undefined` is never sent. */
function sentKeys(search: Record<string, unknown>): string[] {
  return Object.keys(search).filter(key => search[key] !== undefined);
}

/** GetFeed LogRecord fails on these ("GetFeed does not support device ID filtering"). */
const LOG_RECORD_FEED_REJECTED: readonly string[] = ["deviceSearch"];

function feedViolations(typeName: string, search: unknown): Violation[] {
  if (!GET_FEED_TYPES.has(typeName)) {
    return [rejected(`GetFeed does not support typeName "${typeName}"`)];
  }
  // M GetFeed: User (incl. Driver) "not supported" as a search; the server fails even an empty
  // object.
  if ((typeName === "User" || typeName === "Driver") && search !== undefined && search !== null) {
    return [rejected(`GetFeed ${typeName} does not support a search; use Get with a UserSearch`)];
  }
  const applied = GET_FEED_APPLIED_SEARCH[typeName];
  if (applied === undefined || !isRecord(search)) return [];
  return sentKeys(search)
    .filter(key => !applied.includes(key) && key !== "propertySelector")
    .map(key => {
      const message = `GetFeed ${typeName} ignores search property "${key}"`;
      return typeName === "LogRecord" && LOG_RECORD_FEED_REJECTED.includes(key)
        ? rejected(message)
        : ignored(message);
    });
}

function searchViolations(typeName: string, search: Record<string, unknown>): Violation[] {
  const sent = sentKeys(search);
  const violations: Violation[] = [];
  if (sent.includes("propertySelector")) {
    violations.push(ignored("propertySelector belongs next to search, not inside it"));
  }
  if (Array.isArray(search.id)) {
    violations.push(rejected(`${searchName(typeName)} "id" must be a single string`));
  }
  for (const key of UNKNOWN_SEARCH_PROPERTIES[typeName] ?? []) {
    if (sent.includes(key)) violations.push(ignored(`${searchName(typeName)} has no "${key}"`));
  }
  return violations;
}

/**
 * Throws a {@link GeotabCallShapeError} when a JSON-RPC call has a shape the Geotab API ignores
 * or rejects; returns normally otherwise. Pure: no I/O, no logging.
 */
export function assertGeotabCallShape(method: string, params?: Record<string, unknown>): void {
  const typeName = params?.typeName;
  if (typeof typeName !== "string") return;

  const search = params?.search;
  const violations = [
    ...(method === "GetFeed" ? feedViolations(typeName, search) : []),
    ...(isRecord(search) ? searchViolations(typeName, search) : []),
  ];
  if (violations.length > 0) throw new GeotabCallShapeError(method, violations);
}
